// Suppliers & Accounts Payable: per-supplier balance (what you owe), aging,
// statement, record-payment, and edit/delete. The purchasing/AP counterpart to
// the customer/AR side. Sits inside the Inventory module.
import React, { useState, useMemo } from 'react';
import { addInvSupplier, updateInvSupplier, deleteInvSupplier, addInvMovement, postJournal, reverseJournal } from './firebase';
import { fmtMoney } from './insights';

const PAYABLES = '220010'; // Trade Payables (matches the receive-stock flow)

const TH = { padding: '6px 8px', textAlign: 'left', fontSize: 11, color: '#9a9488', borderBottom: '1px solid #e8e4dc' };
const THR = { ...TH, textAlign: 'right' };
const TD = { padding: '6px 8px', fontSize: 12, borderBottom: '1px solid #f0eeea' };
const TDR = { ...TD, textAlign: 'right' };

const inp = { width: '100%', padding: '7px 9px', fontSize: 12, border: '1px solid #e0ddd8', borderRadius: 5, boxSizing: 'border-box', marginTop: 3 };
const btnP = { padding: '8px 14px', borderRadius: 6, border: 'none', background: '#1a1a2e', color: '#c8a24e', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const btnG = { padding: '8px 14px', borderRadius: 6, border: '1px solid #e0ddd8', background: '#fff', color: '#444', fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const card = { background: '#fff', border: '1px solid #eceae4', borderRadius: 8, padding: 14 };
const Field = ({ label, children }) => <div style={{ marginBottom: 8 }}><label style={{ fontSize: 11, color: '#7a7568', fontWeight: 600 }}>{label}</label>{children}</div>;
const Stat = ({ label, value, color }) => (
  <div style={{ background: '#fff', border: '1px solid #eceae4', borderRadius: 8, padding: '10px 14px', minWidth: 130 }}>
    <div style={{ fontSize: 10, color: '#9a9488', textTransform: 'uppercase', letterSpacing: 0.6, fontWeight: 700 }}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 700, color: color || '#1a1a2e', marginTop: 2 }}>{value}</div>
  </div>
);

function bankAccounts(coa) {
  return (coa || []).filter((a) => a.type === 'asset' && (/^1000/.test(a.code) || /bank|cash/i.test(a.name || '')));
}

// FIFO-settle payments against receipts to get outstanding + aging buckets.
function apFor(supplierId, movements, termsDays) {
  const today = new Date();
  const receipts = (movements || [])
    .filter((m) => m.type === 'in' && m.supplierId === supplierId)
    .map((m) => ({ date: m.date || '', gross: Number(m.gross) || Number(m.total) || 0 }))
    .filter((r) => r.gross > 0)
    .sort((a, b) => a.date.localeCompare(b.date));
  const paid = (movements || [])
    .filter((m) => m.type === 'payment' && m.supplierId === supplierId)
    .reduce((s, m) => s + (Number(m.amount) || 0), 0);
  const purchased = receipts.reduce((s, r) => s + r.gross, 0);
  let rem = paid;
  let outstanding = 0;
  const buckets = { current: 0, d30: 0, d60: 0, d90: 0, d90p: 0 };
  receipts.forEach((r) => {
    let unpaid = r.gross;
    if (rem > 0) { const settle = Math.min(rem, unpaid); unpaid -= settle; rem -= settle; }
    if (unpaid > 0.005) {
      outstanding += unpaid;
      const due = new Date(r.date); due.setDate(due.getDate() + (Number(termsDays) || 0));
      const over = Math.floor((today - due) / 86400000);
      if (over <= 0) buckets.current += unpaid;
      else if (over <= 30) buckets.d30 += unpaid;
      else if (over <= 60) buckets.d60 += unpaid;
      else if (over <= 90) buckets.d90 += unpaid;
      else buckets.d90p += unpaid;
    }
  });
  return { purchased, paid, outstanding, buckets, count: receipts.length, lastReceipt: receipts.length ? receipts[receipts.length - 1].date : '' };
}

export function SuppliersAP({ project, coa, suppliers, movements, onReload, onPosted }) {
  const today = new Date().toISOString().slice(0, 10);
  const banks = bankAccounts(coa);
  const acctName = (code) => (coa.find((a) => a.code === code) || {}).name || '';
  const [selId, setSelId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [nf, setNf] = useState({ name: '', contact: '', phone: '', email: '', accountNo: '', terms: 30 });
  const [pay, setPay] = useState({ amount: '', date: today, bank: (banks[0] && banks[0].code) || '100010' });

  const ap = useMemo(() => {
    const map = {};
    (suppliers || []).forEach((s) => { map[s.id] = apFor(s.id, movements, s.terms); });
    return map;
  }, [suppliers, movements]);

  const totalOwed = Object.values(ap).reduce((s, a) => s + a.outstanding, 0);
  const totalOverdue = Object.values(ap).reduce((s, a) => s + a.buckets.d30 + a.buckets.d60 + a.buckets.d90 + a.buckets.d90p, 0);

  const sel = (suppliers || []).find((s) => s.id === selId);
  const selAp = sel ? ap[sel.id] : null;

  function openSupplier(s) {
    setSelId(s.id);
    setDraft({ name: s.name || '', contact: s.contact || '', phone: s.phone || '', email: s.email || '', accountNo: s.accountNo || '', terms: s.terms == null ? 30 : s.terms });
  }

  async function addSupplier() {
    if (!nf.name.trim()) return;
    setBusy(true);
    try { await addInvSupplier(project.id, { ...nf, name: nf.name.trim(), terms: Number(nf.terms) || 0 }); setNf({ name: '', contact: '', phone: '', email: '', accountNo: '', terms: 30 }); if (onReload) await onReload(); }
    catch (e) { alert(e.message); }
    setBusy(false);
  }
  async function saveSupplier() {
    setBusy(true);
    try { await updateInvSupplier(project.id, selId, { ...draft, terms: Number(draft.terms) || 0 }); if (onReload) await onReload(); }
    catch (e) { alert(e.message); }
    setBusy(false);
  }
  async function removeSupplier() {
    if (selAp && selAp.outstanding > 0.005 && !window.confirm('This supplier still has ' + fmtMoney(selAp.outstanding) + ' outstanding. Delete anyway? (history is kept)')) return;
    if (!window.confirm('Delete supplier "' + (sel.name) + '"? Past stock movements keep their record.')) return;
    setBusy(true);
    try { await deleteInvSupplier(project.id, selId); setSelId(null); setDraft(null); if (onReload) await onReload(); }
    catch (e) { alert(e.message); }
    setBusy(false);
  }
  async function recordPayment() {
    const amt = Number(pay.amount) || 0;
    if (amt <= 0) { alert('Enter a positive amount'); return; }
    if (!pay.bank) { alert('Choose a bank account'); return; }
    setBusy(true);
    try {
      const lines = [
        { accountCode: PAYABLES, accountName: acctName(PAYABLES), dr: amt, cr: 0 },
        { accountCode: pay.bank, accountName: acctName(pay.bank), dr: 0, cr: amt },
      ];
      const jid = await postJournal(project.id, { date: pay.date, narration: 'Payment to ' + sel.name + ': ' + fmtMoney(amt), source: { type: 'supplier_payment', supplierId: sel.id }, lines });
      await addInvMovement(project.id, { type: 'payment', supplierId: sel.id, supplierName: sel.name, amount: amt, date: pay.date, bankCode: pay.bank, journalId: jid });
      setPay({ ...pay, amount: '' });
      if (onReload) await onReload();
      if (onPosted) onPosted();
    } catch (e) { alert('Payment failed: ' + e.message); }
    setBusy(false);
  }

  // statement = receipts (+) and payments (−) with running balance owed
  const statement = useMemo(() => {
    if (!sel) return [];
    const rows = (movements || [])
      .filter((m) => m.supplierId === sel.id && (m.type === 'in' || m.type === 'payment'))
      .map((m) => ({
        date: m.date || '',
        label: m.type === 'in' ? ('Received ' + (m.itemName || 'stock') + (m.qty ? ' ×' + m.qty : '')) : 'Payment',
        delta: m.type === 'in' ? (Number(m.gross) || Number(m.total) || 0) : -(Number(m.amount) || 0),
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
    let run = 0;
    rows.forEach((r) => { run += r.delta; r.balance = run; });
    return rows;
  }, [sel, movements]);

  const th = { padding: '6px 8px', textAlign: 'left', fontSize: 11, color: '#9a9488', borderBottom: '1px solid #e8e4dc' };
  const thR = { ...th, textAlign: 'right' };
  const td = { padding: '6px 8px', fontSize: 12, borderBottom: '1px solid #f0eeea' };
  const tdR = { ...td, textAlign: 'right' };

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <Stat label="Suppliers" value={(suppliers || []).length} />
        <Stat label="Total payable" value={fmtMoney(totalOwed)} color={totalOwed > 0 ? '#b3623d' : '#1a1a2e'} />
        <Stat label="Overdue" value={fmtMoney(totalOverdue)} color={totalOverdue > 0 ? '#b33' : '#1a1a2e'} />
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 520px', minWidth: 340, ...card, padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f4f2ed' }}>
              <th style={th}>Supplier</th><th style={th}>Terms</th><th style={thR}>Purchased</th><th style={thR}>Paid</th><th style={thR}>Owed</th>
            </tr></thead>
            <tbody>
              {(suppliers || []).length === 0 && <tr><td style={td} colSpan={5}>No suppliers yet — add one below.</td></tr>}
              {(suppliers || []).map((s) => {
                const a = ap[s.id] || { purchased: 0, paid: 0, outstanding: 0, buckets: {} };
                const overdue = (a.buckets.d30 || 0) + (a.buckets.d60 || 0) + (a.buckets.d90 || 0) + (a.buckets.d90p || 0);
                return (
                  <tr key={s.id} onClick={() => openSupplier(s)} style={{ cursor: 'pointer', background: selId === s.id ? '#faf7f0' : '#fff' }}>
                    <td style={{ ...td, fontWeight: 600, color: '#1a1a2e' }}>{s.name}{s.contact ? <div style={{ fontSize: 10, color: '#9a9488', fontWeight: 400 }}>{s.contact}</div> : null}</td>
                    <td style={td}>{(s.terms == null ? 30 : s.terms) + 'd'}</td>
                    <td style={tdR}>{fmtMoney(a.purchased)}</td>
                    <td style={tdR}>{fmtMoney(a.paid)}</td>
                    <td style={{ ...tdR, color: a.outstanding > 0 ? '#b3623d' : '#1a1a2e', fontWeight: 600 }}>{fmtMoney(a.outstanding)}{overdue > 0.005 ? <span style={{ fontSize: 9, color: '#b33', marginLeft: 4 }}>overdue</span> : null}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {sel && draft ? (
          <div style={{ flex: '0 1 340px', minWidth: 300, ...card }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>{sel.name}</div>
              <button onClick={() => { setSelId(null); setDraft(null); }} style={{ ...btnG, padding: '4px 8px' }}>✕</button>
            </div>

            {/* AP summary + aging */}
            <div style={{ background: '#faf9f6', borderRadius: 6, padding: 10, marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}><span style={{ color: '#7a7568' }}>Owed</span><strong style={{ color: selAp.outstanding > 0 ? '#b3623d' : '#1a1a2e' }}>{fmtMoney(selAp.outstanding)}</strong></div>
              <div style={{ display: 'flex', gap: 4, marginTop: 8, fontSize: 10 }}>
                {[['Current', selAp.buckets.current], ['1–30', selAp.buckets.d30], ['31–60', selAp.buckets.d60], ['61–90', selAp.buckets.d90], ['90+', selAp.buckets.d90p]].map(([l, v]) => (
                  <div key={l} style={{ flex: 1, textAlign: 'center', background: '#fff', borderRadius: 4, padding: '4px 2px', border: '1px solid #eceae4' }}>
                    <div style={{ color: '#9a9488' }}>{l}</div><div style={{ color: v > 0.005 ? (l === 'Current' ? '#3d7a52' : '#b33') : '#c2bdb2', fontWeight: 600 }}>{fmtMoney(v).replace('R ', '')}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Record payment */}
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', marginBottom: 1 }}>Record off-statement payment</div>
              <div style={{ fontSize: 10, color: '#b3623d', marginBottom: 4 }}>Only for payments NOT on an imported bank statement (e.g. cash). If the payment is on a statement you've posted, use the <strong>Match payments</strong> tab instead — otherwise it double-counts.</div>
              <div style={{ display: 'flex', gap: 6 }}>
                <input style={{ ...inp, marginTop: 0 }} type="number" placeholder="Amount" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} />
                <input style={{ ...inp, marginTop: 0, width: 130 }} type="date" value={pay.date} onChange={(e) => setPay({ ...pay, date: e.target.value })} />
              </div>
              <select style={{ ...inp, marginTop: 6 }} value={pay.bank} onChange={(e) => setPay({ ...pay, bank: e.target.value })}>
                {banks.map((b) => <option key={b.code} value={b.code}>{b.code} — {b.name}</option>)}
              </select>
              <button disabled={busy} onClick={recordPayment} style={{ ...btnP, marginTop: 6, width: '100%' }}>{busy ? '…' : 'Pay — dr Payables, cr Bank'}</button>
            </div>

            {/* Statement */}
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>Statement</div>
            <div style={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #eceae4', borderRadius: 6, marginBottom: 12 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {statement.length === 0 && <tr><td style={{ ...td, color: '#9a9488' }}>No activity yet.</td></tr>}
                  {statement.map((r, i) => (
                    <tr key={i}><td style={{ ...td, fontSize: 11 }}>{r.date}</td><td style={{ ...td, fontSize: 11 }}>{r.label}</td><td style={{ ...tdR, fontSize: 11, color: r.delta < 0 ? '#3d7a52' : '#1a1a2e' }}>{r.delta < 0 ? '−' : ''}{fmtMoney(Math.abs(r.delta)).replace('R ', 'R ')}</td><td style={{ ...tdR, fontSize: 11, color: '#9a9488' }}>{fmtMoney(r.balance).replace('R ', '')}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Edit / delete */}
            <div style={{ fontSize: 12, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 }}>Details</div>
            <Field label="Name"><input style={inp} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Field>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><Field label="Contact"><input style={inp} value={draft.contact} onChange={(e) => setDraft({ ...draft, contact: e.target.value })} /></Field></div>
              <div style={{ width: 90 }}><Field label="Terms (days)"><input style={inp} type="number" value={draft.terms} onChange={(e) => setDraft({ ...draft, terms: e.target.value })} /></Field></div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><Field label="Email"><input style={inp} value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></Field></div>
              <div style={{ flex: 1 }}><Field label="Phone"><input style={inp} value={draft.phone} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></Field></div>
            </div>
            <Field label="Account #"><input style={inp} value={draft.accountNo} onChange={(e) => setDraft({ ...draft, accountNo: e.target.value })} /></Field>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              <button disabled={busy} onClick={saveSupplier} style={btnP}>{busy ? 'Saving…' : 'Save'}</button>
              <button disabled={busy} onClick={removeSupplier} style={{ ...btnG, color: '#b33', borderColor: '#e8cccc' }}>Delete</button>
            </div>
          </div>
        ) : (
          <div style={{ flex: '0 1 340px', minWidth: 300, ...card }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>Add supplier</div>
            <Field label="Name"><input style={inp} value={nf.name} onChange={(e) => setNf({ ...nf, name: e.target.value })} /></Field>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><Field label="Contact"><input style={inp} value={nf.contact} onChange={(e) => setNf({ ...nf, contact: e.target.value })} /></Field></div>
              <div style={{ width: 90 }}><Field label="Terms (days)"><input style={inp} type="number" value={nf.terms} onChange={(e) => setNf({ ...nf, terms: e.target.value })} /></Field></div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><Field label="Email"><input style={inp} value={nf.email} onChange={(e) => setNf({ ...nf, email: e.target.value })} /></Field></div>
              <div style={{ flex: 1 }}><Field label="Phone"><input style={inp} value={nf.phone} onChange={(e) => setNf({ ...nf, phone: e.target.value })} /></Field></div>
            </div>
            <Field label="Account #"><input style={inp} value={nf.accountNo} onChange={(e) => setNf({ ...nf, accountNo: e.target.value })} /></Field>
            <button disabled={busy} onClick={addSupplier} style={{ ...btnP, marginTop: 6 }}>{busy ? 'Adding…' : 'Add supplier'}</button>
            <div style={{ fontSize: 10, color: '#9a9488', marginTop: 8 }}>Tip: click a supplier in the list to see their statement, what you owe, and record a payment.</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Bank reconciliation: match posted bank payments to open supplier payables ──
// Settles a payable using the bank line ALREADY posted from the statement (no
// double entry). The original categorisation is reversed (kept in the audit
// trail) and re-posted as dr Trade Payables / cr Bank.
export function MatchPayments({ project, coa, suppliers, movements, journals, onReload, onPosted }) {
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState({});
  const banks = bankAccounts(coa);
  const bankSet = useMemo(() => new Set(banks.map((b) => b.code)), [coa]);
  const acctName = (code) => (coa.find((a) => a.code === code) || {}).name || '';

  const matchedIds = useMemo(() => new Set((movements || []).filter((m) => m.matchedBankJournalId).map((m) => m.matchedBankJournalId)), [movements]);

  const candidates = useMemo(() => {
    return (journals || []).filter((j) => {
      if (j.reverses) return false;
      const src = (j.source && j.source.type) || '';
      if (['supplier_payment', 'inventory_in', 'inventory_out'].indexOf(src) >= 0) return false;
      if (matchedIds.has(j.id)) return false;
      return (j.lines || []).some((l) => bankSet.has(l.accountCode) && (Number(l.cr) || 0) > 0);
    }).map((j) => {
      const bankLines = (j.lines || []).filter((l) => bankSet.has(l.accountCode) && (Number(l.cr) || 0) > 0);
      const amount = bankLines.reduce((s, l) => s + (Number(l.cr) || 0), 0);
      const contra = (j.lines || []).filter((l) => !bankSet.has(l.accountCode) && (Number(l.dr) || 0) > 0).map((l) => acctName(l.accountCode) || l.accountCode).join(', ');
      return { j, amount, bankCode: (bankLines[0] || {}).accountCode, contra };
    }).sort((a, b) => (b.j.date || '').localeCompare(a.j.date || ''));
  }, [journals, matchedIds, bankSet]);

  const openSuppliers = useMemo(() => (suppliers || []).map((s) => ({ s, ap: apFor(s.id, movements, s.terms) })).filter((x) => x.ap.outstanding > 0.005), [suppliers, movements]);

  async function doMatch(c) {
    const supplierId = pick[c.j.id];
    const sup = (suppliers || []).find((s) => s.id === supplierId);
    if (!sup) { alert('Pick a supplier to match'); return; }
    if (!window.confirm('Match ' + fmtMoney(c.amount) + ' to ' + sup.name + '? The original entry will be reversed (kept in the audit trail) and re-posted as a payment clearing their payable.')) return;
    setBusy(true);
    try {
      await reverseJournal(project.id, c.j.id, 'Re-matched as payment to ' + sup.name);
      const jid = await postJournal(project.id, {
        date: c.j.date,
        narration: 'Payment to ' + sup.name + ' (matched from bank)',
        source: { type: 'supplier_payment', supplierId: sup.id, matchedFrom: c.j.id },
        lines: [
          { accountCode: PAYABLES, accountName: acctName(PAYABLES), dr: c.amount, cr: 0 },
          { accountCode: c.bankCode, accountName: acctName(c.bankCode), dr: 0, cr: c.amount },
        ],
      });
      await addInvMovement(project.id, { type: 'payment', supplierId: sup.id, supplierName: sup.name, amount: c.amount, date: c.j.date, bankCode: c.bankCode, journalId: jid, matchedBankJournalId: c.j.id });
      if (onReload) await onReload();
      if (onPosted) onPosted();
    } catch (e) { alert('Match failed: ' + e.message); }
    setBusy(false);
  }

  return (
    <div>
      <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e' }}>Match bank payments to suppliers</div>
      <div style={{ fontSize: 11, color: '#9a9488', marginTop: 2, marginBottom: 12 }}>Settle a supplier's payable using the bank payment already posted from your statement — no double-entry. The original categorisation is reversed (kept in the audit trail) and re-posted as <strong>dr Trade Payables / cr Bank</strong>.</div>
      {openSuppliers.length === 0 && <div style={{ fontSize: 12, color: '#9a9488', marginBottom: 10 }}>No suppliers have an open balance — receive stock on credit first, then their payments will be matchable here.</div>}
      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ background: '#f4f2ed' }}>
            <th style={TH}>Date</th><th style={TH}>Bank payment</th><th style={TH}>Currently</th><th style={THR}>Amount</th><th style={TH}>Match to supplier</th><th style={TH}></th>
          </tr></thead>
          <tbody>
            {candidates.length === 0 && <tr><td style={TD} colSpan={6}>No unmatched bank payments. (Only money-out lines from imported statements appear here.)</td></tr>}
            {candidates.slice(0, 200).map((c) => (
              <tr key={c.j.id}>
                <td style={{ ...TD, fontSize: 11 }}>{c.j.date}</td>
                <td style={{ ...TD, fontSize: 11 }}>{c.j.narration}</td>
                <td style={{ ...TD, fontSize: 11, color: '#9a9488' }}>{c.contra || '—'}</td>
                <td style={{ ...TDR, fontWeight: 600 }}>{fmtMoney(c.amount)}</td>
                <td style={TD}>
                  <select value={pick[c.j.id] || ''} onChange={(e) => setPick({ ...pick, [c.j.id]: e.target.value })} style={{ fontSize: 11, padding: '4px 6px', border: '1px solid #e0ddd8', borderRadius: 4, maxWidth: 200 }}>
                    <option value="">— supplier —</option>
                    {openSuppliers.map(({ s, ap }) => <option key={s.id} value={s.id}>{s.name} (owe {fmtMoney(ap.outstanding)})</option>)}
                  </select>
                </td>
                <td style={TD}><button disabled={busy || !pick[c.j.id]} onClick={() => doMatch(c)} style={{ ...btnP, padding: '5px 10px' }}>Match</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: 10, color: '#9a9488', marginTop: 8 }}>Showing money-out lines from posted statements that aren't yet matched. Matching reverses the original entry and clears the payable — fully reversible from the Audit Trail.</div>
    </div>
  );
}
